if (!customElements.get('ikaslandi-hero')) {
  class IkaslandiHero extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.touchStartX = null;
      this.isPointerInside = false;
      this.isFocusInside = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.previousButton = this.querySelector('[data-hero-previous]');
      this.nextButton = this.querySelector('[data-hero-next]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.loop = this.dataset.loop === 'true';
      this.delay = Number(this.dataset.autoplayDelay) || 5000;

      if (!this.slides.length) return;

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.showSlide(0, false);
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    showSlide(index, restart = true) {
      if (!this.slides.length) return;

      const boundedIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.currentIndex = boundedIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === boundedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));

        slide.querySelectorAll('[data-hero-focusable]').forEach((element) => {
          element.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        slide.querySelectorAll('video').forEach((video) => {
          if (isActive && !this.reducedMotion.matches) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === boundedIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });

      if (restart) this.restartAutoplay();
    }

    move(direction) {
      const lastIndex = this.slides.length - 1;
      let nextIndex = this.currentIndex + direction;

      if (this.loop) {
        nextIndex = (nextIndex + this.slides.length) % this.slides.length;
      } else {
        nextIndex = Math.max(0, Math.min(nextIndex, lastIndex));
      }

      this.showSlide(nextIndex);
    }

    onPrevious() {
      this.move(-1);
    }

    onNext() {
      this.move(1);
    }

    onDotClick(event) {
      this.showSlide(Number(event.currentTarget.dataset.slideIndex));
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.showSlide(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.showSlide(this.slides.length - 1);
      }
    }

    onMouseEnter() {
      this.isPointerInside = true;
      this.stopAutoplay();
    }

    onMouseLeave() {
      this.isPointerInside = false;
      this.startAutoplay();
    }

    onFocusIn() {
      this.isFocusInside = true;
      this.stopAutoplay();
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.isFocusInside = false;
      this.startAutoplay();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0]?.clientX ?? null;
      this.stopAutoplay();
    }

    onTouchEnd(event) {
      if (this.touchStartX === null) return;
      const touchEndX = event.changedTouches[0]?.clientX ?? this.touchStartX;
      const distance = touchEndX - this.touchStartX;
      this.touchStartX = null;

      if (Math.abs(distance) > 45) this.move(distance > 0 ? -1 : 1);
      this.startAutoplay();
    }

    onBlockSelect(event) {
      const block = event.target.closest?.('[data-hero-slide]');
      if (!block || !this.contains(block)) return;

      this.stopAutoplay();
      this.showSlide(Number(block.dataset.slideIndex), false);
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.startAutoplay();
    }

    startAutoplay() {
      if (
        !this.autoplay ||
        this.slides.length < 2 ||
        this.reducedMotion.matches ||
        document.hidden ||
        this.isPointerInside ||
        this.isFocusInside ||
        window.Shopify?.designMode
      ) {
        return;
      }

      this.stopAutoplay();
      this.timer = window.setInterval(() => this.move(1), this.delay);
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('ikaslandi-hero', IkaslandiHero);
}
